import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { Location, LocationCreatePayload, LocationUpdatePayload } from '@mira-avant/mtlibs';
import { ApiService } from './api.service';

@Injectable({
	providedIn: 'root',
})
export class LocationService {
	private readonly BASE_PATH = 'locations';

	public loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

	constructor(private api: ApiService) {}

	private async withLoading<T>(operation: () => Promise<T>): Promise<T> {
		this.loading$.next(true);
		try {
			return await operation();
		} finally {
			this.loading$.next(false);
		}
	}

	public sortLocations(locations: Location[]): Location[] {
		return locations.sort((a: Location, b: Location) => {
			if (a.display_order !== b.display_order) {
				return a.display_order - b.display_order;
			}
			return a.name.localeCompare(b.name);
		});
	}

	public async getByClient$(
		clientUuid: string,
		page: number,
		pageSize: number
	): Promise<Location[]> {
		return this.withLoading(async () => {
			const locations = await firstValueFrom(
				this.api.get<Location[]>(
					`${this.BASE_PATH}/client/${clientUuid}/${page * pageSize}/${pageSize}`
				)
			);

			const sortedLocations = this.sortLocations(locations);
			return sortedLocations;
		});
	}

	public async hasLocations(clientUuid: string): Promise<boolean> {
		return this.withLoading(async () => {
			const hasLocations = await firstValueFrom(
				this.api.get<boolean>(`${this.BASE_PATH}/client/${clientUuid}/has-locations`)
			);

			return hasLocations;
		});
	}

	public async searchLocations(clientUuid: string, searchTerm: string): Promise<Location[]> {
		if (!searchTerm) {
			console.warn('Search term is required');
			return [];
		}

		return this.withLoading(async () => {
			const results = await firstValueFrom(
				this.api.get<Location[]>(`${this.BASE_PATH}/search/${clientUuid}/${searchTerm}`)
			);

			return results;
		});
	}

	public async getNameByUuid$(uuid: string): Promise<string> {
		return this.withLoading(async () => {
			const name = await firstValueFrom(
				this.api.get<string>(`${this.BASE_PATH}/name/${uuid}`)
			);

			return name;
		});
	}

	public async getTotalLocations(clientUuid: string): Promise<number> {
		return this.withLoading(async () => {
			return await firstValueFrom<number>(
				this.api.get<number>(`${this.BASE_PATH}/client/${clientUuid}/total-locations`)
			);
		});
	}

	public async get$(uuid: string): Promise<Location> {
		return this.withLoading(async () => {
			return await firstValueFrom(
				this.api.get<Location>(`${this.BASE_PATH}/get-by-uuid/${uuid}`)
			);
		});
	}

	public async create$(locationData: LocationCreatePayload): Promise<Location> {
		return this.withLoading(async () => {
			const newLocation = await firstValueFrom(
				this.api.post<Location>(this.BASE_PATH, locationData)
			);

			return newLocation;
		});
	}

	public async update$(uuid: string, locationData: LocationUpdatePayload): Promise<Location> {
		return this.withLoading(async () => {
			const updatedLocation = await firstValueFrom(
				this.api.put<Location>(`${this.BASE_PATH}/${uuid}`, locationData)
			);

			return updatedLocation;
		});
	}

	public async delete$(uuid: string): Promise<void> {
		return this.withLoading(async () => {
			await firstValueFrom(this.api.delete<void>(`${this.BASE_PATH}/${uuid}`));
		});
	}

	public async moveUp$(uuid: string, clientUuid: string): Promise<void> {
		await this.withLoading(async () => {
			await firstValueFrom(
				this.api.post<Location[]>(`${this.BASE_PATH}/move-up/${uuid}/${clientUuid}`, {})
			);
		});
	}

	public async moveDown$(uuid: string, clientUuid: string): Promise<void> {
		await this.withLoading(async () => {
			await firstValueFrom(
				this.api.post<Location[]>(`${this.BASE_PATH}/move-down/${uuid}/${clientUuid}`, {})
			);
		});
	}

	public async addUserToLocation$(locationUuid: string, cognitoUuiduid: string): Promise<void> {
		return this.withLoading(async () => {
			await firstValueFrom(
				this.api.post<void>(
					`${this.BASE_PATH}/${locationUuid}/link-user/${cognitoUuiduid}`,
					{}
				)
			);
		});
	}

	public async removeUserFromLocation$(locationUuid: string, cognitoUuid: string): Promise<void> {
		return this.withLoading(async () => {
			await firstValueFrom(
				this.api.delete<void>(
					`${this.BASE_PATH}/${locationUuid}/unlink-user/${cognitoUuid}`
				)
			);
		});
	}
}
